import type { CashState } from './model';
import { ChangeCashMode } from './model';
import type { Gender } from '@pflegenavi/shared/api';
import { sumCashStateInCentsByType } from '../euro';
import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { Iconify, LoadingPromiseButton } from '@pflegenavi/web-components';
import type { CashListIconComponent } from '@pflegenavi/web-components';
import { ChangeSummaryCard } from './cards/ChangeSummaryCard';
import { ResidentPreviewCard } from './cards/ResidentPreviewCard';
import { CashNotesField } from './inputs/CashNotesField';

export interface ChangeCashFormConfirmationProps {
  cashState: CashState;
  mode: ChangeCashMode;
  handleClose: VoidFunction;
  previousStep: VoidFunction;
  residentData?: {
    name: string;
    balanceInCents: number;
    gender: Gender;
  };
  bankAccountChange?: number;
  skippedCashSelection: boolean;
  skippedAmount?: number;
  confirm: () => Promise<void>;
  skip: () => Promise<void>;

  requireNote?: boolean;

  cashListName: string | undefined;
  cashListIcon: CashListIconComponent | undefined;
  /** Current total balance of the cash list in cents; undefined while loading. */
  currentBalance: number | undefined;

  notes: string;
  setNotes: React.Dispatch<React.SetStateAction<string>>;

  showResidentPreview?: boolean;

  /** Show the "Beleg herunterladen" checkbox (ReceiptWithdrawal only). */
  showDownloadReceiptOption?: boolean;
  downloadReceipt: boolean;
  setDownloadReceipt: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ChangeCashFormConfirmation: FC<
  ChangeCashFormConfirmationProps
> = ({
  mode,
  cashState,
  handleClose,
  previousStep,
  residentData,
  confirm,
  requireNote,
  skippedAmount,
  bankAccountChange,
  skippedCashSelection,
  skip,
  cashListName,
  cashListIcon,
  currentBalance,
  notes,
  setNotes,
  showResidentPreview,
  showDownloadReceiptOption,
  downloadReceipt,
  setDownloadReceipt,
}) => {
  const { t } = useTranslation();

  const [promise, setPromise] = useState<Promise<unknown> | undefined>(
    undefined
  );

  const { total } = useMemo(() => {
    return sumCashStateInCentsByType(cashState);
  }, [cashState]);

  // Signed net change across cash and bank: cashState/bankAccountChange are
  // positive magnitudes for deposit/withdraw and already-signed deltas for
  // adjust.
  const changeAmount =
    (mode === ChangeCashMode.Withdraw ? -1 : 1) *
    (total + (bankAccountChange ?? 0));

  const [notesIsError, setNotesIsError] = useState(false);
  const isNotesRequired =
    requireNote === undefined
      ? mode === ChangeCashMode.Adjust && total + (bankAccountChange ?? 0) !== 0
      : requireNote;
  const isReadyToSubmit = isNotesRequired ? notes.length > 1 : true;

  const onClick = useCallback(() => {
    if (!isReadyToSubmit) {
      setNotesIsError(true);
      return;
    }

    if (!skippedCashSelection) {
      const promise = confirm();
      promise.finally(() => {
        setPromise(undefined);
      });
      setPromise(promise);
    } else {
      const promise = skip();
      promise.finally(() => {
        setPromise(undefined);
      });
      setPromise(promise);
    }
  }, [isReadyToSubmit, confirm, skippedCashSelection, skip]);

  return (
    <Stack
      direction="column"
      sx={{
        gap: 2,
      }}
    >
      <Typography variant="h6">
        {t('cashManagement.change-confirmation')}
      </Typography>
      <Stack
        direction={
          showResidentPreview && !skippedCashSelection ? 'column' : 'row'
        }
        spacing={2}
        sx={{
          alignItems:
            showResidentPreview && !skippedCashSelection ? '' : 'flex-end',
          justifyContent: 'space-between',
        }}
      >
        {!skippedCashSelection ? (
          <Stack direction="row" spacing={2}>
            {cashListName !== undefined &&
              cashListIcon !== undefined &&
              currentBalance !== undefined && (
                <ChangeSummaryCard
                  mode={mode}
                  cashListName={cashListName}
                  Icon={cashListIcon}
                  currentBalance={currentBalance}
                  amount={changeAmount}
                />
              )}

            {showResidentPreview && residentData && (
              <ResidentPreviewCard
                mode={mode}
                total={changeAmount}
                resident={residentData}
              />
            )}
          </Stack>
        ) : (
          showResidentPreview &&
          residentData && (
            <Stack direction="row" spacing={2}>
              <ResidentPreviewCard
                mode={mode}
                total={
                  (mode === ChangeCashMode.Withdraw ? -1 : 1) *
                  (skippedAmount ?? 0)
                }
                resident={residentData}
              />
            </Stack>
          )
        )}

        <Box sx={{ flex: 1 }}>
          <CashNotesField
            notes={notes}
            setNotes={setNotes}
            error={notesIsError}
            setError={setNotesIsError}
            required={isNotesRequired}
            rows={showResidentPreview && !skippedCashSelection ? 3 : 6.3}
          />
        </Box>
      </Stack>
      {skippedCashSelection && (
        <Stack
          direction="row"
          sx={{
            gap: 1,
            alignItems: 'stretch',
            mt: 2,
          }}
        >
          <Alert severity="warning" sx={{ width: '100%' }}>
            <Typography variant="body2" sx={{ pt: 0.2 }}>
              {t('cashManagement.skip-cash-selection-warning')}
            </Typography>
          </Alert>
        </Stack>
      )}
      <Stack
        direction="row"
        sx={{
          gap: 1,
          justifyContent: showDownloadReceiptOption ? 'space-between' : 'end',
          alignItems: 'center',
        }}
      >
        {showDownloadReceiptOption && (
          <FormControlLabel
            control={
              <Checkbox
                data-cy="download-receipt-checkbox"
                checked={downloadReceipt}
                onChange={(e) => setDownloadReceipt(e.target.checked)}
              />
            }
            label={t(
              'cashManagement.group-detail.cash-transaction-card.download-pdf'
            )}
          />
        )}
        <Stack
          direction="row"
          sx={{
            gap: 1,
          }}
        >
          <Button variant="outlined" color="primary" onClick={previousStep}>
            {t('cashManagement.change-selection')}
          </Button>
          <LoadingPromiseButton
            data-cy="confirm-cash-mgmt-button"
            promise={promise}
            sx={{ width: 140 }}
            variant="contained"
            startIcon={
              skippedCashSelection && <Iconify icon="eva:alert-triangle-fill" />
            }
            endIcon={
              !skippedCashSelection && <Iconify icon="eva:checkmark-fill" />
            }
            onClick={onClick}
            disabled={!isReadyToSubmit}
          >
            {t('cashManagement.confirm')}
          </LoadingPromiseButton>
        </Stack>
      </Stack>
    </Stack>
  );
};
